import React, { useState } from "react";
import {
    TagIcon,
    PlusIcon,
    MagnifyingGlassIcon,
    PencilIcon,
    TrashIcon,
    XMarkIcon,
} from "@heroicons/react/24/outline";
import {
    useCategories,
    useCreateCategory,
    useUpdateCategory,
    useDeleteCategory,
} from "../../Hooks/useCategories";
import { canAccess } from "../../utils/permissions";

export default function Category() {
    const [searchTerm, setSearchTerm] = useState("");
    const [isAdding, setIsAdding] = useState(false);
    const [newName, setNewName] = useState("");

    const [editingId, setEditingId] = useState(null);
    const [editName, setEditName] = useState("");

    // ✅ Delete Modal State
    const [isDeleteOpen, setIsDeleteOpen] = useState(false);
    const [deleteId, setDeleteId] = useState(null);
    const [deleteName, setDeleteName] = useState("");

    const { data: categories = [], isLoading } = useCategories();
    const createMutation = useCreateCategory();
    const updateMutation = useUpdateCategory();
    const deleteMutation = useDeleteCategory();

    const canCreateCategory = canAccess("create_category");
    const canUpdateCategory = canAccess("update_category");
    const canDeleteCategory = canAccess("delete_category");

    if (!canAccess("view_categories")) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[60vh] p-8 text-center text-sm font-medium">
                <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mb-6">
                    <TagIcon className="w-10 h-10 text-red-600" />
                </div>
                <h2 className="text-3xl font-black text-gray-900 mb-2 tracking-tight">
                    Access Restricted
                </h2>
                <p className="text-gray-500 max-w-md font-medium">
                    You don't have the required permissions to view or manage categories.
                </p>
            </div>
        );
    }

    const filtered = (categories || []).filter((c) =>
        (c?.name || "").toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleAdd = () => {
        if (!canCreateCategory) return;
        if (!newName.trim()) return;

        createMutation.mutate({
            name: newName.trim(),
            parent_id: null,
        });

        setIsAdding(false);
        setNewName("");
    };

    const handleUpdate = (id) => {
        if (!canUpdateCategory) return;
        updateMutation.mutate({ id, payload: { name: editName } });
        setEditingId(null);
    };

    // ✅ بدل window.confirm: افتح مودال تأكيد
    const openDeleteModal = (cat) => {
        if (!canDeleteCategory) return;
        setDeleteId(cat.id);
        setDeleteName(cat.name || "this category");
        setIsDeleteOpen(true);
    };

    const closeDeleteModal = () => {
        setIsDeleteOpen(false);
        setDeleteId(null);
        setDeleteName("");
    };

    const confirmDelete = () => {
        if (!deleteId) return;
        deleteMutation.mutate(deleteId, {
            onSuccess: () => closeDeleteModal(),
            onError: () => {
                // خلي المودال مفتوح لو حبيتي، أو سكّريه
                // closeDeleteModal();
            },
        });
    };

    const totalSubcategories = (categories || []).reduce(
        (sum, c) => sum + (c?.children?.length || 0),
        0
    );

    return (
        <section className="space-y-8 py-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900">Main Categories</h1>
                    <p className="text-gray-600 mt-1">Manage primary inspection categories</p>
                </div>

                <div className="flex gap-3">
                    <div className="relative">
                        <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search categories..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-10 pr-4 py-3 w-full sm:w-64 border rounded-xl focus:ring-2 focus:ring-[#1665f6] outline-none"
                        />
                    </div>

                    {canCreateCategory && (
                        <button
                            onClick={() => setIsAdding(true)}
                            className="px-5 py-3 bg-gradient-to-r from-[#1665f6] to-[#8830b8] text-white rounded-xl font-bold flex items-center gap-2 shadow-lg hover:shadow-xl"
                        >
                            <PlusIcon className="w-5 h-5" />
                            Add Category
                        </button>
                    )}
                </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
                <div className="bg-gradient-to-r from-[#1665f6] to-[#0d51d4] rounded-2xl p-6 text-white shadow-lg">
                    <TagIcon className="w-10 h-10 mb-3 opacity-90" />
                    <p className="text-sm opacity-90">Total Categories</p>
                    <p className="text-3xl font-bold mt-2">{categories.length}</p>
                </div>

                <div className="bg-gradient-to-r from-[#8830b8] to-purple-700 rounded-2xl p-6 text-white shadow-lg">
                    <p className="text-sm opacity-90">Total Subcategories</p>
                    <p className="text-3xl font-bold mt-2">{totalSubcategories}</p>
                </div>

                <div className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-2xl p-6 text-white shadow-lg">
                    <p className="text-sm opacity-90">Active</p>
                    <p className="text-3xl font-bold mt-2">{categories.length}</p>
                </div>
            </div>

            {/* Categories Grid */}
            {isLoading ? (
                <div className="text-center py-20 text-gray-500">Loading categories...</div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filtered.map((cat) => {
                        const title = cat?.name || "Unnamed";
                        const childrenCount = cat?.children?.length || 0;

                        return (
                            <div
                                key={cat.id}
                                className="bg-white rounded-2xl shadow-sm border p-6 hover:shadow-lg transition"
                            >
                                <div className="flex items-center justify-between mb-4">
                                    <div className="flex items-center gap-3">
                                        <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-gray-100 text-[#1665f6] font-bold text-lg">
                                            {(title[0] || "?").toUpperCase()}
                                        </div>

                                        <div>
                                            {editingId === cat.id ? (
                                                <input
                                                    type="text"
                                                    value={editName}
                                                    onChange={(e) => setEditName(e.target.value)}
                                                    className="font-bold text-gray-900 border-b-2 border-[#1665f6] outline-none"
                                                    autoFocus
                                                />
                                            ) : (
                                                <h3 className="font-bold text-gray-900">{title}</h3>
                                            )}

                                            <p className="text-xs text-gray-600">
                                                {childrenCount} subcategories
                                            </p>
                                        </div>
                                    </div>

                                    <div className="flex gap-2">
                                        {editingId === cat.id ? (
                                            <>
                                                {canUpdateCategory && (
                                                    <button
                                                        onClick={() => handleUpdate(cat.id)}
                                                        className="p-2 bg-green-100 text-green-700 rounded-lg"
                                                    >
                                                        Save
                                                    </button>
                                                )}
                                                <button
                                                    onClick={() => setEditingId(null)}
                                                    className="p-2 bg-gray-100 rounded-lg"
                                                >
                                                    Cancel
                                                </button>
                                            </>
                                        ) : (
                                            <>
                                                {canUpdateCategory && (
                                                    <button
                                                        onClick={() => {
                                                            setEditingId(cat.id);
                                                            setEditName(title);
                                                        }}
                                                        className="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg"
                                                    >
                                                        <PencilIcon className="w-4 h-4 text-gray-600" />
                                                    </button>
                                                )}

                                                {canDeleteCategory && (
                                                    <button
                                                        onClick={() => openDeleteModal(cat)}
                                                        className="p-2 bg-red-100 hover:bg-red-200 rounded-lg"
                                                    >
                                                        <TrashIcon className="w-4 h-4 text-red-600" />
                                                    </button>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Add Modal */}
            {
                isAdding && canCreateCategory && (
                    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
                        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
                            <div className="flex justify-between items-center mb-6">
                                <h2 className="text-xl font-bold">Add New Category</h2>
                                <button onClick={() => setIsAdding(false)}>
                                    <XMarkIcon className="w-6 h-6" />
                                </button>
                            </div>

                            <div className="space-y-5">
                                <input
                                    type="text"
                                    value={newName}
                                    onChange={(e) => setNewName(e.target.value)}
                                    placeholder="Category name"
                                    className="w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-[#1665f6] outline-none"
                                />
                            </div>

                            <div className="flex justify-end gap-3 mt-6">
                                <button
                                    onClick={() => setIsAdding(false)}
                                    className="px-5 py-3 border rounded-xl font-bold hover:bg-gray-50"
                                >
                                    Cancel
                                </button>

                                <button
                                    onClick={handleAdd}
                                    disabled={createMutation.isPending}
                                    className="px-5 py-3 bg-gradient-to-r from-[#1665f6] to-[#8830b8] text-white rounded-xl font-bold shadow-lg hover:shadow-xl disabled:opacity-70"
                                >
                                    {createMutation.isPending ? "Adding..." : "Add Category"}
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* ✅ Delete Confirm Modal */}
            {
                isDeleteOpen && canDeleteCategory && (
                    <div
                        className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
                        onClick={closeDeleteModal}
                    >
                        <div
                            className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="flex justify-between items-center mb-4">
                                <h2 className="text-xl font-bold text-gray-900">Confirm Delete</h2>
                                <button onClick={closeDeleteModal}>
                                    <XMarkIcon className="w-6 h-6" />
                                </button>
                            </div>

                            <p className="text-sm text-gray-600">
                                Are you sure you want to delete{" "}
                                <span className="font-bold text-gray-900">{deleteName}</span>?
                                <br />
                                <span className="text-red-600 font-semibold">
                                    This will also delete all subcategories.
                                </span>
                            </p>

                            <div className="flex justify-end gap-3 mt-6">
                                <button
                                    onClick={closeDeleteModal}
                                    className="px-5 py-3 border rounded-xl font-bold hover:bg-gray-50"
                                >
                                    Cancel
                                </button>

                                <button
                                    onClick={confirmDelete}
                                    disabled={deleteMutation.isPending}
                                    className="px-5 py-3 bg-red-600 text-white rounded-xl font-bold shadow-lg hover:bg-red-700 disabled:opacity-70"
                                >
                                    {deleteMutation.isPending ? "Deleting..." : "Delete"}
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }
        </section >
    );
}
